/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';
import { readStarted, readFinished } from 'shared/lib/readTracker';

/** Internal Components. */
import { getSocket } from 'socket';
import { formatDateToDateString } from 'shared/lib/dateFormatter';

export const NOT_CONNECTED_MESSAGE =
    'Not connected to the server — the change was not saved. Please try again once the connection is restored.';

/**
 * A save attempted while the socket is down must not look like it worked: without
 * the connection the request would sit in the client's buffer with no answer, the
 * screen would keep the edit and the operator would move on believing it was saved.
 * Blocks the save with a warning instead, and hands the caller its failure path.
 */
export const requireConnectedSocket = (dispatch, onFailure) => {
    const socket = getSocket();
    if (socket?.connected) return socket;
    dispatch(
        setSnackbar({
            open: true,
            message: NOT_CONNECTED_MESSAGE,
            severity: 'warning',
        })
    );
    onFailure?.();
    return null;
};

/**
 * Asks the server for the rows a screen shows. A read made while the link is down
 * is not refused: it goes out the moment the connection returns, so the screen
 * fills in without the operator doing anything. Each read is also counted (outside
 * Redux) so the connection badge can tell when the screens have caught up after a
 * reconnection.
 */
export const readSocketData = (
    dispatch,
    tabValue,
    unit,
    entity,
    reducer,
    searchValue,
    onSuccess,
    onFailure,
    onFinally
) => {
    const socket = getSocket();
    if (!socket) {
        onFinally?.();
        return;
    }
    readStarted();
    socket.emit('read', tabValue, unit, entity, reducer, searchValue, (response) => {
        console.log(`Socket ${entity} response:`, response);
        if (response.status === 200) {
            onSuccess?.(response.data);
        } else {
            dispatch(
                setSnackbar({
                    open: true,
                    message: response.message,
                    severity: 'error',
                })
            );
            onFailure?.();
        }
        onFinally?.();
        readFinished();
    });
};

/**
 * Saves a change to one row. When the server turns it down, onFailure receives the response so
 * the screen can react to the reason — e.g. drop a row a colleague already deleted.
 */
export const updateSocketData = (
    dispatch,
    tabValue,
    unit,
    entity,
    reducer,
    sendValue,
    onSuccess,
    onFailure,
    onFinally
) => {
    const socket = requireConnectedSocket(dispatch, () => {
        onFailure?.();
        onFinally?.();
    });
    if (!socket) return;

    socket.emit('update', tabValue, unit, entity, reducer, sendValue, (response) => {
        console.log(`Socket ${entity} response:`, response);
        if (response.status === 200) {
            onSuccess?.(response);
        } else {
            onFailure?.(response);
            dispatch(
                setSnackbar({
                    open: true,
                    message: response.message,
                    severity: 'error',
                })
            );
        }
        onFinally?.();
    });
};

export const createSocketData = (dispatch, tabValue, unit, entity, reducer, sendValue, onSuccess, onFailure) => {
    const socket = requireConnectedSocket(dispatch, onFailure);
    if (!socket) return;
    socket.emit('create', tabValue, unit, entity, reducer, sendValue, (response) => {
        console.log(`Socket ${entity} response:`, response);
        if (response.status === 200) {
            onSuccess?.(response);
        } else {
            onFailure?.(response);
            dispatch(
                setSnackbar({
                    open: true,
                    message: response.message,
                    severity: 'error',
                })
            );
        }
    });
};

const BULK_CREATE_ACK_PER_ROW_MS = 100;
const BULK_CREATE_ACK_MIN_MS = 10000;
const BULK_CREATE_ACK_MAX_MS = 60000;

export const bulkCreateAckTimeout = (rowCount) =>
    Math.min(Math.max(rowCount * BULK_CREATE_ACK_PER_ROW_MS, BULK_CREATE_ACK_MIN_MS), BULK_CREATE_ACK_MAX_MS);

const BULK_CREATE_UNKNOWN_OUTCOME_MESSAGE =
    'The server did not answer. Please try again.';

/** Register multiple shipments at once (bulk/Excel-like create) in a single all-or-nothing transaction. */
export const createBulkSocketData = (dispatch, tabValue, unit, entity, reducer, rows, onSuccess, onFailure) => {
    const socket = requireConnectedSocket(dispatch, onFailure);
    if (!socket) return;

    let settled = false;
    const onDisconnect = () => giveUp();
    const settle = (finish) => {
        if (settled) return;
        settled = true;
        socket.off?.('disconnect', onDisconnect);
        finish();
    };

    function giveUp() {
        settle(() => {
            onFailure?.();
            dispatch(
                setSnackbar({
                    open: true,
                    message: BULK_CREATE_UNKNOWN_OUTCOME_MESSAGE,
                    severity: 'error',
                    autoHideDuration: 10000,
                })
            );
        });
    }

    socket.on?.('disconnect', onDisconnect);

    socket
        .timeout(bulkCreateAckTimeout(rows.length))
        .emit('create_bulk', tabValue, unit, entity, reducer, { rows }, (timeoutError, response) => {
            console.log(`Socket ${entity} bulk response:`, timeoutError ?? response);
            if (timeoutError) {
                giveUp();
                return;
            }
            settle(() => {
                if (response.status === 200) {
                    onSuccess?.(response);
                } else {
                    onFailure?.(response);
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: response.message,
                            severity: 'error',
                        })
                    );
                }
            });
        });
};

/** Deletes rows. Like updateSocketData, onFailure receives the server's response when it refuses. */
export const deleteSocketData = (dispatch, tabValue, unit, entity, reducer, sendValue, onSuccess, onFailure) => {
    const socket = requireConnectedSocket(dispatch, onFailure);
    if (!socket) return;
    socket.emit('delete', tabValue, unit, entity, reducer, sendValue, (response) => {
        console.log(`Socket ${entity} response:`, response);
        if (response.status === 200) {
            onSuccess?.(response.data);
        } else {
            onFailure?.(response);
            dispatch(
                setSnackbar({
                    open: true,
                    message: response.message,
                    severity: 'error',
                })
            );
        }
    });
};

// DateTime fields use ISO format; the rest of dateField uses YYYY-MM-DD.
const DATE_TIME_FIELDS = [
    'registered_on',
    'modified_on',
    'pick_appointment',
    'prepull_appointment',
    'delivery_appointment',
];
const QUANTITY_FIELDS = ['hbl_quantity', 'quantity', 'weight', 'alias_quantity'];

// Persists one changed field of a row. Pasting a horizontally-copied range
// (e.g. from Excel) changes several fields of one row at once, but the server
// accepts one cell per update — so the row-level handler below runs this once
// per changed field, letting every pasted cell reach the server instead of
// only the first. Rejected fields queue their reason in validationErrors so
// the user gets one warning for the whole paste, not one dialog per cell.
const applyFieldUpdate = ({
    socket,
    dispatch,
    tabValue,
    methodTableName,
    reducerName,
    tableId,
    key,
    value,
    accRow,
    notAllowNullKey,
    dateField,
    onReturn,
    validationErrors,
}) => {
    // An empty cell pasted into a date column means "clear this date". By the
    // time it reaches here the empty text has already become an "Invalid Date"
    // object, which would otherwise be saved as garbage ("NaN-NaN-NaN") or
    // crash on toISOString() — treat it as an intentional clear.
    if (dateField.includes(key) && value instanceof Date && isNaN(value.getTime())) {
        value = null;
    }

    // Validation for non-null keys
    if ((value === null || value === '') && notAllowNullKey.includes(key)) {
        validationErrors.push(`'${key}' requires a value.`);
        return Promise.resolve(accRow);
    }

    // Empty value handling - when a user clears a cell (e.g. pressing Delete/Backspace),
    // the value comes in as an empty string. Convert it to null so the server receives null.
    if (value === '') {
        value = null;
    }

    // Date format convert - datetime fields use ISO format, date fields use YYYY-MM-DD
    if (dateField.includes(key)) {
        if (DATE_TIME_FIELDS.includes(key)) {
            // DateTime fields: use ISO format
            value = value ? value.toISOString() : null;
        } else {
            // Date fields: use YYYY-MM-DD format — 달력 날짜라 UTC 변환 없이 로컬 연·월·일 그대로.
            value = value ? formatDateToDateString(value) : null;
        }
    }

    // Validate if key is 'volume' or 'weight' and check if the value is a positive integer.
    // Skip when value is null (a cleared cell) so it can be saved as null.
    if (QUANTITY_FIELDS.includes(key) && value !== null && !/^\d+$/.test(value)) {
        validationErrors.push(`'${key}' must be a non-negative integer.`);
        return Promise.resolve(accRow); // Keep the row as-is if the validation fails
    }

    return new Promise((resolve) => {
        socket.emit(
            'update',
            tabValue,
            'cell',
            methodTableName,
            reducerName,
            { id: accRow.id, key: key, value: value, table_id: tableId },
            (response) => {
                console.log('Socket response:', response);
                if (response.status !== 200) {
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: response.message,
                            severity: 'warning',
                        })
                    );
                }
                if (onReturn) {
                    // Contract: onReturn must resolve() on every branch — a skipped
                    // branch silently discards the user's remaining pasted cells in
                    // this row. On failure it should resolve accRow: fields the server
                    // already saved must stay on screen, or the grid would show less
                    // than what was actually persisted.
                    onReturn(response, { ...accRow, [key]: value }, accRow, resolve, key);
                } else {
                    // Default behavior: keep the field's new value on success, revert it on failure.
                    const updatedRow = response.status === 200 ? { ...accRow, [key]: value } : accRow;
                    resolve(updatedRow);
                }
            }
        );
    });
};

/**
 * Handle a DataGrid row update: detect changed fields against formData, then
 * validate and persist each changed field sequentially with one 'cell' socket
 * emit per field (a multi-cell paste into one row changes several fields at once).
 *
 * @param {function} dispatch - Redux dispatch function
 * @param {number} tabValue - Current tab index
 * @param {string} methodTableName - Table name for the socket 'update' emit
 * @param {string} reducerName - Reducer name for the socket 'update' emit
 * @param {array} tableId - Parent table id chain for the socket 'update' emit
 * @param {array} formData - Full form data list (source of the pre-edit row)
 * @param {object} newRow - New row data from DataGrid edit
 * @param {array} notAllowNullKey - Fields that must not be cleared
 * @param {array} dateField - Fields converted to date strings before emit
 * @param {function} [onReturn] - Per-field override of the response handling:
 *   (response, updatedRow, prevRow, resolve, key). Must call resolve() on every
 *   branch; resolve prevRow (not the pre-paste row) to reject just this field.
 * @returns {Promise<object>} Promise resolving to the row with every accepted
 *   field applied and every rejected field reverted
 */
export const handleProcessRowUpdate = ({
    dispatch,
    tabValue,
    methodTableName,
    reducerName,
    tableId,
    formData,
    callBack,
    newRow,
    notAllowNullKey,
    dateField,
    onReturn,
}) => {
    const oldRow = formData.find((row) => row.id === newRow.id);

    // Detect changes
    const changes = Object.keys(newRow).reduce((acc, key) => {
        if (JSON.stringify(newRow[key]) !== JSON.stringify(oldRow[key])) {
            acc[key] = newRow[key];
        }
        return acc;
    }, {});

    const changedKeys = Object.keys(changes);
    if (changedKeys.length === 0) return Promise.resolve(newRow);

    // Offline: the cell goes back to what the server has, so the operator never
    // walks away from a grid showing a value that was not saved.
    const socket = requireConnectedSocket(dispatch);
    if (!socket) return Promise.resolve(oldRow);

    // A multi-cell paste into a single row (e.g. pasting a horizontally-copied
    // range) produces multiple changed keys at once. Since updates are persisted
    // one cell at a time, apply each changed field sequentially so every pasted
    // cell is validated and saved, not just the first.
    const validationErrors = [];
    return changedKeys
        .reduce(
            (promise, key) =>
                promise.then((accRow) =>
                    applyFieldUpdate({
                        socket,
                        dispatch,
                        tabValue,
                        methodTableName,
                        reducerName,
                        tableId,
                        key,
                        value: changes[key],
                        accRow,
                        notAllowNullKey,
                        dateField,
                        onReturn,
                        validationErrors,
                    })
                ),
            Promise.resolve(oldRow)
        )
        .then((finalRow) => {
            // One combined warning per row so a multi-cell paste doesn't stack
            // a dialog/snackbar per rejected field.
            if (validationErrors.length > 0) {
                dispatch(
                    setSnackbar({
                        open: true,
                        message: `Not saved — ${validationErrors.join(' ')}`,
                        severity: 'warning',
                    })
                );
            }
            return finalRow;
        });
};
